// Üzenet-címzés lista-cella — a legacy CRM „Címzettek" oszlopának 1:1 megfelelője az új designban. A legacy
// egyetlen recipient-dimenziót mutatott (mindenki / minden ügyfél / kollégák / fiókok / kontaktok /
// szolgáltatások / részlegek), a specifikusakat egy modalban listázva. Itt:
//   • AUDIENCE-/CSOPORT-badge-ek (mindenki · minden ügyfél · N fiók/szolgáltatás/részleg) — a szórás-szintű
//     és a kereszt-namespace (NÉV nélküli, csak darabszám) dimenziók,
//   • NÉVVEL feloldott identity-címzettek (dolgozó/kontakt) — avatar + név, a KÖZÖS `ExpandableCell`-ben
//     (>3 → legördülő), a meetings-résztvevő cella mintájára.
// Ha egyik dimenzió sincs → halvány em-dash. Üzenet-specifikus molekula → components/messages/.
import { UserGroupIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

import { ExpandableCell } from '@/components/shared/expandable-cell';
import { PersonChip } from '@/components/shared/person-chip';
import { Badge } from '@/components/ui/badge';

export interface MessageRecipient {
  id: string;
  /** `null` = az identity nem oldható fel (törölt személy) → „Törölt felhasználó" címke. */
  name: string | null;
  /** Strukturált név-részek — a nyelv szerinti névsorrendhez (a `PersonChip` formáz). */
  givenName: string | null;
  familyName: string | null;
  /** Presigned avatar-URL vagy null (monogram-fallback). */
  avatarUrl: string | null;
  /** A kolléga csapat-színe — az avatar háttere, mint a többi listán (külsősnél `null`). */
  teamColor?: string | null;
  kind: string;
}
export interface MessageRecipientGroup {
  kind: string;
  count: number;
  /** A feloldott célok nevei (plafonos, ábécé-rendben) — a badge tooltipje. Üres lehet (a `count` a mérvadó). */
  names: string[];
}

export interface MessageRecipientsCellProps {
  recipients: MessageRecipient[];
  groups: MessageRecipientGroup[];
  everyone: boolean;
  allClients: boolean;
}

export function MessageRecipientsCell({
  recipients,
  groups,
  everyone,
  allClients,
}: MessageRecipientsCellProps): JSX.Element {
  const { t } = useTranslation();

  // Szórás-/csoport-badge-ek: a szórás-szintű flagek + a kereszt-namespace csoportok (darabszám a badge-en,
  // a feloldott NEVEK a tooltipben). Miért nem a cellában sorolva: egy „minden fiók" címzés 300+ név lenne,
  // a sor pedig egy LISTA-cella — a darabszám a scannelhető információ, a nevek a megerősítés.
  const badges: { key: string; label: string; title?: string }[] = [];
  if (everyone) badges.push({ key: 'everyone', label: t('messages.audience.everyone') });
  if (allClients) badges.push({ key: 'allClients', label: t('messages.audience.allClients') });
  for (const g of groups) {
    const more = g.count - g.names.length;
    const title =
      g.names.length === 0
        ? undefined
        : more > 0
          ? `${g.names.join(', ')} ${t('messages.more', { count: more })}`
          : g.names.join(', ');
    badges.push({
      key: g.kind,
      label: t(`messages.recipientGroup.${g.kind}`, { count: g.count, defaultValue: g.kind }),
      ...(title === undefined ? {} : { title }),
    });
  }

  if (badges.length === 0 && recipients.length === 0) {
    return <span className="text-muted-foreground">—</span>;
  }

  return (
    <div className="min-w-0 space-y-1.5">
      {badges.length > 0 && (
        <div className="flex flex-wrap items-center gap-1">
          {badges.map((b) => (
            <Badge key={b.key} variant="muted" title={b.title} icon={<UserGroupIcon />}>
              {b.label}
            </Badge>
          ))}
        </div>
      )}
      {recipients.length > 0 && (
        <ExpandableCell
          items={recipients}
          getKey={(r) => r.id}
          visible={2}
          threshold={3}
          moreLabel={(n) => t('messages.more', { count: n })}
          triggerAriaLabel={t('messages.moreRecipients')}
          renderItem={(r) => (
            // Avatar + NYELVHELYES név (a support-csapatok „Tagok" oszlopának mintája): a `PersonChip` a
            // strukturált név-részekből formáz, tehát magyarul vezetéknév elöl.
            <PersonChip
              name={r.name ?? t('common.deletedUser')}
              givenName={r.givenName}
              familyName={r.familyName}
              avatarUrl={r.avatarUrl}
              accentColor={r.teamColor ?? null}
              size="sm"
              className="min-w-0 flex-1 gap-1.5"
            />
          )}
        />
      )}
    </div>
  );
}
